import React, { useState } from 'react';
import moment from 'moment';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import LoaderOverlay from './LoaderOverlay';
import DatePicker from './DatePicker';
import PasswordInput from './PasswordInput';
import PhoneInputField from './PhoneInputField';
import SourceCombobox from './SourceCombobox';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  VALIDATION_MESSAGES,
  REGEX_PATTERNS,
} from '../../utils/validationMessages';

const SOURCE_OPTIONS = ['Website', 'Social Media'] as const;

interface CreateClientFormData {
  fullName: string;
  email: string;
  dob: string;
  source: string;
  phone: string;
  password: string;
}

interface CreateClientDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const INITIAL_FORM_DATA: CreateClientFormData = {
  fullName: '',
  email: '',
  dob: '',
  source: '',
  phone: '',
  password: '',
};

const CreateClientDrawer: React.FC<CreateClientDrawerProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] =
    useState<CreateClientFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateClientFormData, string>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof CreateClientFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof CreateClientFormData, string>> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = VALIDATION_MESSAGES.fullName.required;
    }

    if (!formData.email.trim()) {
      newErrors.email = VALIDATION_MESSAGES.email.required;
    } else if (!REGEX_PATTERNS.email.test(formData.email.trim())) {
      newErrors.email = VALIDATION_MESSAGES.email.invalid;
    }

    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required.';
    } else {
      const dobDate = moment(formData.dob, 'YYYY-MM-DD');
      if (dobDate.isValid()) {
        const age = moment().diff(dobDate, 'years');
        if (age < 18) {
          newErrors.dob = 'You must be at least 18 years old.';
        }
      }
    }

    if (!formData.source) {
      newErrors.source = 'Please select a source.';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    } else if (formData.phone.replace(/\D/g, '').length < 7) {
      newErrors.phone = 'Please enter a valid phone number.';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required.';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setIsSubmitting(true);
      const dobFormatted = formData.dob
        ? moment(formData.dob, 'YYYY-MM-DD').format('DD-MM-YYYY')
        : undefined;

      await adminAPI.createClient({
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        password: formData.password,
        dob: dobFormatted,
        source: formData.source || undefined,
      });

      toast.success(
        'Client account created! A welcome email with login credentials has been sent.'
      );

      resetForm();
      onClose();
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to create client:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create client. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <LoaderOverlay
          isLoading={isSubmitting}
          loadingText="Creating client..."
        >
          <SheetHeader>
            <SheetTitle>Add New Client</SheetTitle>
            <SheetDescription>
              Fill in the details below to create a new client account.
            </SheetDescription>
          </SheetHeader>

          <form
            id="create-client-form"
            onSubmit={handleSubmit}
            noValidate
            className="py-6 space-y-5"
          >
            <div className="space-y-1.5">
              <Label htmlFor="create-client-source">
                Source <span className="text-destructive">*</span>
              </Label>
              <SourceCombobox
                id="create-client-source"
                items={SOURCE_OPTIONS}
                placeholder="Select source..."
                value={formData.source}
                onChange={value => handleChange('source', value)}
                disabled={isSubmitting}
                emptyText="No matching source found."
              />
              {errors.source && (
                <p className="text-xs text-destructive">{errors.source}</p>
              )}
            </div>
            {/* Full Name */}
            <div className="space-y-1.5">
              <Label htmlFor="create-client-fullName">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="create-client-fullName"
                type="text"
                placeholder="Enter your full name as per Aadhar & PAN"
                value={formData.fullName}
                onChange={e => handleChange('fullName', e.target.value)}
                disabled={isSubmitting}
                aria-invalid={!!errors.fullName}
              />
              {errors.fullName && (
                <p className="text-xs text-destructive">{errors.fullName}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="create-client-email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="create-client-email"
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={e => handleChange('email', e.target.value)}
                disabled={isSubmitting}
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email}</p>
              )}
            </div>

            {/* Date of Birth */}
            <div className="space-y-1.5">
              <Label htmlFor="dob">
                Date of Birth <span className="text-destructive">*</span>
              </Label>
              <DatePicker
                id="dob"
                placeholder="Pick a Birth Date"
                value={formData.dob}
                onChange={value => handleChange('dob', value)}
                aria-invalid={!!errors.dob}
              />
              {errors.dob && (
                <p className="text-xs text-destructive">{errors.dob}</p>
              )}
            </div>

            {/* Phone – uses common PhoneInputField component */}
            <div className="space-y-1.5">
              <Label htmlFor="create-client-phone">
                Phone Number <span className="text-destructive">*</span>
              </Label>
              <PhoneInputField
                id="create-client-phone"
                value={formData.phone}
                onChange={phone => handleChange('phone', phone)}
                disabled={isSubmitting}
                placeholder="Enter phone number"
                hasError={!!errors.phone}
              />
              {errors.phone && (
                <p className="text-xs text-destructive">{errors.phone}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="create-client-password">
                Password <span className="text-destructive">*</span>
              </Label>
              <PasswordInput
                id="create-client-password"
                name="create-client-password"
                placeholder="Create password"
                value={formData.password}
                onChange={e => handleChange('password', e.target.value)}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password}</p>
              )}
            </div>
          </form>

          <SheetFooter className="mt-2 gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="create-client-form"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Client'}
            </Button>
          </SheetFooter>
        </LoaderOverlay>
      </SheetContent>
    </Sheet>
  );
};

export default CreateClientDrawer;
